<template>
  <div>
    <v-row>
      <v-col cols="12">
        <p class="text-2xl">
          Canais WhatsApp
        </p>
        <v-card flat>
          <v-card-text class="pa-3">
            <v-row>
              <v-col
                cols="12"
                sm="12"
                md="3"
              >
                <v-text-field
                  v-model="filterName"
                  :append-icon="icons.mdiMagnify"
                  label="Nome do canal"
                  hide-details
                  outlined
                  dense
                  @keypress.enter="fetchChannels"
                />
              </v-col>
            </v-row>
          </v-card-text>
          <v-divider />
          <v-card-actions class="pa-3">
            <v-btn
              color="primary"
              class="pl-4"
              @click="fetchChannels"
            >
              Pesquisar
              <v-icon
                right
                dark
              >
                {{ icons.mdiMagnify }}
              </v-icon>
            </v-btn>
            <v-btn
              color="primary"
              class="pl-4"
              outlined
              @click="openFormToCreate"
            >
              Cadastrar Canal
              <v-icon
                right
                dark
              >
                {{ icons.mdiTablePlus }}
              </v-icon>
            </v-btn>
          </v-card-actions>
        </v-card>

        <v-card
          flat
          class="mt-5"
        >
          <v-data-table
            :headers="headers"
            :items="filteredChannels"
            item-key="id"
            class="table-rounded"
            :loading="loading"
            disable-sort
            dense
          >
            <template #[`item.profileName`]="{ item }">
              <div class="d-flex align-center py-2">
                <v-avatar
                  size="36"
                  class="mr-3"
                  color="grey lighten-2"
                >
                  <img
                    v-if="getChannelImageUrl(item)"
                    :src="getChannelImageUrl(item)"
                    alt="Foto do canal"
                  >
                  <v-icon
                    v-else
                    small
                  >
                    {{ icons.mdiAccountCircleOutline }}
                  </v-icon>
                </v-avatar>
                <span class="font-weight-semibold">{{ item.profileName }}</span>
              </div>
            </template>

            <template #[`item.channelPhone`]="{ item }">
              {{ item.channelPhone || '-' }}
            </template>

            <template #[`item.status`]="{ item }">
              <v-chip
                class="my-2"
                :color="resolveStatus(item.status).color"
                small
                label
              >
                <span class="white--text font-weight-semibold">
                  {{ resolveStatus(item.status).text }}
                </span>
              </v-chip>
            </template>

            <template #[`item.createdAt`]="{ item }">
              {{ item.createdAt ? $day(item.createdAt).format('DD/MM/YYYY HH:mm') : '-' }}
            </template>

            <template #no-data>
              Nenhum canal cadastrado.
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <validation-observer
      ref="formChannel"
      autocomplete="off"
      tag="form"
    >
      <dialog-form
        :status="formDialogStatus"
        :is-update="false"
        :title="'Cadastrar Canal'"
        :width="600"
        @action="fetchAction"
        @close="closeForm"
      >
        <v-row>
          <v-col
            cols="12"
            md="12"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Nome de exibição"
              vid="profileName"
              rules="required"
            >
              <v-text-field
                v-model="form.profileName"
                :error-messages="errors"
                label="Nome de exibição"
                hint="Nome que aparecerá para os destinatários"
                persistent-hint
                outlined
                dense
              />
            </validation-provider>
          </v-col>

          <v-col
            cols="12"
            md="12"
          >
            <p class="mb-2 text-sm">
              Foto de perfil
            </p>
            <input
              ref="profilePhotoFile"
              type="file"
              accept="image/*"
              class="d-none"
              @change="handleProfilePhotoChange"
            >
            <v-btn
              color="primary"
              outlined
              small
              :disabled="profilePhotoLoading"
              @click="$refs.profilePhotoFile.click()"
            >
              <v-icon left>
                {{ icons.mdiImagePlus }}
              </v-icon>
              {{ form.profilePhoto ? 'Alterar Foto' : 'Selecionar Foto' }}
            </v-btn>

            <div
              v-if="profilePhotoPreviewUrl"
              class="mt-3 d-flex align-center"
            >
              <v-avatar
                size="56"
                class="mr-3"
              >
                <img
                  :src="profilePhotoPreviewUrl"
                  alt="Pré-visualização"
                >
              </v-avatar>
              <span class="text-sm">{{ profilePhotoFileName }}</span>
            </div>
          </v-col>
        </v-row>
      </dialog-form>
    </validation-observer>
  </div>
</template>

<script>
import {
  mdiMagnify,
  mdiTablePlus,
  mdiImagePlus,
  mdiAccountCircleOutline,
} from '@mdi/js'
import { mapActions } from 'vuex'

const CHANNEL_STATUS = {
  1: { text: 'Pendente', color: 'warning' },
  2: { text: 'Em análise', color: 'info' },
  3: { text: 'Aprovado', color: 'success' },
  4: { text: 'Recusado', color: 'error' },
}

export default {
  data() {
    return {
      icons: {
        mdiMagnify,
        mdiTablePlus,
        mdiImagePlus,
        mdiAccountCircleOutline,
      },
      loading: false,
      filterName: '',
      channels: [],
      headers: [
        { text: 'Canal', value: 'profileName' },
        { text: 'Telefone', value: 'channelPhone' },
        { text: 'Status', value: 'status' },
        { text: 'Cadastrado em', value: 'createdAt' },
      ],
      formDialogStatus: false,
      form: {
        profileName: '',
        profilePhoto: null,
      },
      profilePhotoFileName: '',
      profilePhotoPreviewUrl: '',
      profilePhotoLoading: false,
    }
  },
  computed: {
    filteredChannels() {
      const term = (this.filterName || '').trim().toLowerCase()
      if (!term) return this.channels
      return this.channels.filter(channel => (channel.profileName || '').toLowerCase().includes(term))
    },
  },
  created() {
    this.fetchChannels()
  },
  methods: {
    ...mapActions('serviceWhatsapp', [
      'findAllChannelRequests',
      'createChannelRequest',
    ]),

    resolveStatus(status) {
      return CHANNEL_STATUS[status] || { text: 'Desconhecido', color: 'secondary' }
    },

    getChannelImageUrl(channel) {
      const fileId = channel.idfileProfileImage
      if (!fileId) return ''
      return `${this.$axios.defaults.baseURL}/public/files/${fileId}`
    },

    async fetchChannels() {
      this.loading = true
      try {
        const data = await this.findAllChannelRequests()
        this.channels = Array.isArray(data) ? data : []
      } finally {
        this.loading = false
      }
    },

    openFormToCreate() {
      this.resetForm()
      this.formDialogStatus = true
    },

    closeForm() {
      this.formDialogStatus = false
      this.resetForm()
    },

    resetForm() {
      if (this.profilePhotoPreviewUrl) {
        URL.revokeObjectURL(this.profilePhotoPreviewUrl)
      }
      this.form = {
        profileName: '',
        profilePhoto: null,
      }
      this.profilePhotoFileName = ''
      this.profilePhotoPreviewUrl = ''
      if (this.$refs.profilePhotoFile) {
        this.$refs.profilePhotoFile.value = ''
      }
      if (this.$refs.formChannel) {
        this.$refs.formChannel.reset()
      }
    },

    handleProfilePhotoChange(event) {
      const file = event.target.files && event.target.files[0]
      if (!file) return

      if (this.profilePhotoPreviewUrl) {
        URL.revokeObjectURL(this.profilePhotoPreviewUrl)
      }

      this.form.profilePhoto = file
      this.profilePhotoFileName = file.name
      this.profilePhotoPreviewUrl = URL.createObjectURL(file)
    },

    async fetchAction() {
      const isValid = await this.$refs.formChannel.validate()
      if (!isValid) {
        this.$notify('Preencha os campos obrigatórios.', 'error')
        return
      }

      if (!this.form.profilePhoto) {
        this.$notify('Selecione uma foto de perfil para o canal.', 'error')
        return
      }

      const status = await this.createChannelRequest({
        profileName: this.form.profileName,
        profilePhoto: this.form.profilePhoto,
      })

      if (status === 201) {
        this.closeForm()
        this.fetchChannels()
      }
    },
  },
}
</script>
